import { useEffect } from 'react';
import { X, ExternalLink, Info, Zap, Gift, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

const TYPE_STYLES = {
  info:         { icon: Info,            bg: 'bg-sky-500/10',    border: 'border-sky-500/30',    iconColor: 'text-sky-400' },
  beta_update:  { icon: Zap,             bg: 'bg-purple-500/10', border: 'border-purple-500/30', iconColor: 'text-purple-400' },
  promo:        { icon: Gift,            bg: 'bg-emerald-500/10',border: 'border-emerald-500/30',iconColor: 'text-emerald-400' },
  warning:      { icon: AlertTriangle,   bg: 'bg-amber-500/10',  border: 'border-amber-500/30',  iconColor: 'text-amber-400' },
};

export function AnnouncementModal({ announcement, onDismiss }) {
  const { announcement_id, title, message, image_url, cta_text, cta_link, announcement_type, priority } = announcement;
  const style = TYPE_STYLES[announcement_type] || TYPE_STYLES.info;
  const Icon = style.icon;

  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && priority !== 'high') onDismiss(announcement_id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [announcement_id, priority, onDismiss]);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-background/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget && priority !== 'high') onDismiss(announcement_id);
      }}
      data-testid="announcement-modal-overlay"
    >
      <div
        className={`relative flex flex-col rounded-2xl ${style.bg} ${style.border} border-2 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300`}
        style={{ width: '90vw', maxWidth: 500, maxHeight: '80vh' }}
        data-testid={`announcement-modal-${announcement_id}`}
      >
        {/* Close X — always visible */}
        <button
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-background/50 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background/70 transition-colors"
          onClick={() => onDismiss(announcement_id)}
          data-testid="announcement-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto overscroll-contain min-h-0">
          {/* Image */}
          {image_url && (
            <div className="w-full max-h-44 overflow-hidden flex-shrink-0">
              <img
                src={image_url.startsWith('/') ? `${window.location.origin}${image_url}` : image_url}
                alt=""
                className="w-full h-full object-cover"
                data-testid="announcement-image"
              />
            </div>
          )}

          {/* Text content */}
          <div className="p-5 pb-2">
            <div className="flex items-start gap-3 mb-3">
              <div className={`w-9 h-9 rounded-lg ${style.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <Icon className={`w-5 h-5 ${style.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-foreground leading-tight break-words" data-testid="announcement-title">{title}</h2>
              </div>
            </div>

            <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap break-words" data-testid="announcement-message">
              {message}
            </p>
          </div>
        </div>

        {/* Sticky footer — always visible */}
        <div className="flex-shrink-0 p-4 pt-3 border-t border-border bg-inherit" data-testid="announcement-actions">
          <div className="flex items-center gap-3">
            {cta_text && cta_link && (
              <a href={cta_link} target="_blank" rel="noopener noreferrer" className="flex-1">
                <Button className="w-full bg-white text-black hover:bg-white/90 font-semibold text-sm" data-testid="announcement-cta">
                  {cta_text}
                  <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </a>
            )}
            <Button
              className={`${cta_text && cta_link ? '' : 'flex-1'} bg-muted/50 hover:bg-muted/60 text-white font-medium text-sm`}
              onClick={() => onDismiss(announcement_id)}
              data-testid="announcement-dismiss-btn"
            >
              {priority === 'high' ? 'I Understand' : 'Dismiss'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
